(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-products-slider]';
  const SLIDE_SELECTOR = '.products-slider__slide';

  if (window.DrhProductsSlider?.initScope) {
    window.DrhProductsSlider.initScope(document);
    return;
  }

  function rootsInScope(scope) {
    if (!scope) return [];
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(root => roots.push(root));
    return roots;
  }

  function addListener(cleanups, target, type, listener, options) {
    target.addEventListener(type, listener, options);
    cleanups.push(() => target.removeEventListener(type, listener, options));
  }

  function parseNumber(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function routesRoot(root) {
    const configured = root.dataset.routesRoot || window.Shopify?.routes?.root || '/';
    return configured.endsWith('/') ? configured : `${configured}/`;
  }

  function abortError(error) {
    return error?.name === 'AbortError';
  }

  class CarouselController {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-track]');
      this.previousButton = root.querySelector('[data-slider-prev]');
      this.nextButton = root.querySelector('[data-slider-next]');
      this.trackWrapper = root.querySelector('.products-slider__track-wrapper');
      this.slides = this.track ? Array.from(this.track.querySelectorAll(SLIDE_SELECTOR)) : [];
      this.currentIndex = 0;
      this.wheelDelta = 0;
      this.autoplayTimer = null;
      this.wheelSnapTimer = null;
      this.scrollEndTimer = null;
      this.resizeTimer = null;
      this.focusSyncFrame = null;
      this.cleanups = [];
      try {
        this.hovered = root.matches(':hover');
      } catch {
        this.hovered = false;
      }
      this.focusWithin = root.contains(document.activeElement);
      this.inViewport = true;
      this.loading = root.querySelector('[data-products-slider-panel]')?.getAttribute('aria-busy') === 'true';
      this.destroyed = false;
      this.visibilityObserver = null;
      this.desktopMode = this.isDesktop();
      this.motionQuery = typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

      this.cardsDesktop = parseNumber(root.dataset.cardsDesktop, 6);
      this.cardsMobile = parseNumber(root.dataset.cardsMobile, 2);
      this.scrollCountDesktop = parseNumber(root.dataset.scrollCountDesktop, 1);
      this.scrollCountMobile = parseNumber(root.dataset.scrollCountMobile, 1);
      this.autoslideDesktop = root.dataset.autoslideDesktop === 'true';
      this.autoslideMobile = root.dataset.autoslideMobile === 'true';
      this.autoslideIntervalDesktop = parseNumber(root.dataset.autoslideIntervalDesktop, 5000);
      this.autoslideIntervalMobile = parseNumber(root.dataset.autoslideIntervalMobile, 5000);

      this.init();
    }

    isDesktop() {
      return window.innerWidth >= 768;
    }

    visibleCards() {
      return this.isDesktop() ? this.cardsDesktop : this.cardsMobile;
    }

    maxIndex() {
      return Math.max(0, this.slides.length - this.visibleCards());
    }

    scrollCount() {
      return this.isDesktop() ? this.scrollCountDesktop : this.scrollCountMobile;
    }

    needsSlider() {
      return this.slides.length > this.visibleCards();
    }

    shouldAutoplay() {
      const enabled = this.isDesktop() ? this.autoslideDesktop : this.autoslideMobile;
      return enabled && !(this.motionQuery?.matches) && this.needsSlider() && !this.hovered && !this.focusWithin && this.inViewport && !this.loading;
    }

    stopAutoplay() {
      if (this.autoplayTimer !== null) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.shouldAutoplay() || this.destroyed) return;
      const interval = this.isDesktop() ? this.autoslideIntervalDesktop : this.autoslideIntervalMobile;
      this.autoplayTimer = window.setInterval(() => this.goNext(), interval);
    }

    setLoading(loading) {
      this.loading = loading;
      this.updateControls();
      if (loading) this.stopAutoplay();
      else this.startAutoplay();
    }

    slideUnit() {
      if (!this.slides[0]) return 0;
      return this.slides[0].offsetWidth + (this.isDesktop() ? 16 : 12);
    }

    updatePosition(animate = true) {
      if (!this.track || this.slides.length === 0) return;
      if (!this.needsSlider()) {
        this.syncSlideAccessibility();
        return;
      }
      const offset = this.currentIndex * this.slideUnit();
      const shouldAnimate = animate && !(this.motionQuery?.matches);
      if (this.isDesktop()) {
        this.track.style.transition = shouldAnimate ? 'transform 0.4s ease' : 'none';
        this.track.style.transform = `translateX(-${offset}px)`;
      } else if (typeof this.track.scrollTo === 'function') {
        this.track.scrollTo({ left: offset, behavior: shouldAnimate ? 'smooth' : 'instant' });
      }
      this.syncSlideAccessibility();
    }

    handleMotionPreferenceChange() {
      this.stopAutoplay();
      this.updatePosition(false);
      if (!(this.motionQuery?.matches)) this.startAutoplay();
    }

    goNext() {
      if (!this.needsSlider()) return;
      const maximum = this.maxIndex();
      this.currentIndex = this.currentIndex >= maximum
        ? 0
        : Math.min(this.currentIndex + this.scrollCount(), maximum);
      this.updatePosition();
    }

    goPrevious() {
      if (!this.needsSlider()) return;
      this.currentIndex = this.currentIndex <= 0
        ? this.maxIndex()
        : Math.max(this.currentIndex - this.scrollCount(), 0);
      this.updatePosition();
    }

    syncSlideAccessibility() {
      const desktop = this.isDesktop();
      const visibleEnd = this.currentIndex + this.visibleCards();
      const activeElement = document.activeElement;

      this.slides.forEach((slide, index) => {
        const outsideWindow = desktop && (index < this.currentIndex || index >= visibleEnd);
        const containsFocus = activeElement && slide.contains(activeElement);
        if (outsideWindow && !containsFocus) {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
        } else {
          slide.removeAttribute('inert');
          if (desktop) slide.setAttribute('aria-hidden', 'false');
          else slide.removeAttribute('aria-hidden');
        }
      });
    }

    updateControls() {
      const needed = this.needsSlider();
      [this.previousButton, this.nextButton].forEach(button => {
        if (!button) return;
        button.hidden = !needed;
        button.disabled = !needed || this.loading;
      });

      if (this.track) this.track.style.overflowX = needed ? '' : 'hidden';
      if (!needed) {
        this.currentIndex = 0;
        this.stopAutoplay();
        if (this.track) {
          this.track.style.transform = '';
          this.track.style.transition = '';
        }
      }
      this.syncSlideAccessibility();
    }

    initVisibilityObserver() {
      if (typeof window.IntersectionObserver !== 'function') return;
      this.visibilityObserver = new window.IntersectionObserver(entries => {
        const entry = entries.find(item => item.target === this.root);
        if (!entry) return;
        this.inViewport = entry.isIntersecting;
        if (this.inViewport) this.startAutoplay();
        else this.stopAutoplay();
      }, { threshold: 0 });
      this.visibilityObserver.observe(this.root);
    }

    init() {
      if (!this.track || this.slides.length === 0) {
        this.updateControls();
        return;
      }

      if (this.previousButton) {
        addListener(this.cleanups, this.previousButton, 'click', () => {
          this.goPrevious();
          this.stopAutoplay();
        });
      }
      if (this.nextButton) {
        addListener(this.cleanups, this.nextButton, 'click', () => {
          this.goNext();
          this.stopAutoplay();
        });
      }

      addListener(this.cleanups, this.root, 'mouseenter', () => {
        this.hovered = true;
        this.stopAutoplay();
      });
      addListener(this.cleanups, this.root, 'mouseleave', () => {
        this.hovered = false;
        this.startAutoplay();
      });
      addListener(this.cleanups, this.root, 'focusin', () => {
        this.focusWithin = true;
        this.stopAutoplay();
      });
      addListener(this.cleanups, this.root, 'focusout', event => {
        window.cancelAnimationFrame(this.focusSyncFrame);
        this.focusSyncFrame = window.requestAnimationFrame(() => {
          this.focusSyncFrame = null;
          if (!this.destroyed) this.syncSlideAccessibility();
        });
        if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
        this.focusWithin = false;
        this.startAutoplay();
      });

      if (this.motionQuery) {
        addListener(this.cleanups, this.motionQuery, 'change', () => this.handleMotionPreferenceChange());
      }

      if (this.trackWrapper) {
        addListener(this.cleanups, this.trackWrapper, 'wheel', event => {
          if (!this.needsSlider() || !this.isDesktop()) return;
          const horizontal = Math.abs(event.deltaX);
          if (horizontal < 5 || horizontal < Math.abs(event.deltaY)) return;

          event.preventDefault();
          this.stopAutoplay();
          const unit = this.slideUnit();
          const maximum = this.maxIndex() * unit;
          const current = this.currentIndex * unit;
          this.wheelDelta += event.deltaX;
          const provisional = Math.max(0, Math.min(current + this.wheelDelta, maximum));
          this.track.style.transition = 'none';
          this.track.style.transform = `translateX(-${provisional}px)`;

          window.clearTimeout(this.wheelSnapTimer);
          this.wheelSnapTimer = window.setTimeout(() => {
            this.currentIndex = Math.max(0, Math.min(Math.round(provisional / unit), this.maxIndex()));
            this.wheelDelta = 0;
            this.updatePosition();
            this.startAutoplay();
          }, 120);
        }, { passive: false });
      }

      addListener(this.cleanups, this.track, 'touchstart', () => this.stopAutoplay(), { passive: true });
      addListener(this.cleanups, this.track, 'scroll', () => {
        this.stopAutoplay();
        if (!this.isDesktop()) {
          const unit = this.slideUnit();
          if (unit > 0) {
            this.currentIndex = Math.max(0, Math.min(Math.round(this.track.scrollLeft / unit), this.maxIndex()));
          }
        }
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => {
          if (!this.isDesktop()) {
            const unit = this.slideUnit();
            if (unit > 0) {
              this.currentIndex = Math.max(0, Math.min(Math.round(this.track.scrollLeft / unit), this.maxIndex()));
            }
          }
          this.startAutoplay();
        }, 2000);
      }, { passive: true });
      addListener(this.cleanups, window, 'resize', () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          const desktop = this.isDesktop();
          const crossedBreakpoint = desktop !== this.desktopMode;
          this.desktopMode = desktop;
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex());
          this.wheelDelta = 0;
          if (crossedBreakpoint) {
            this.currentIndex = 0;
            this.track.style.transform = '';
            this.track.style.transition = '';
            this.track.scrollLeft = 0;
          }
          this.updateControls();
          this.updatePosition(false);
          this.startAutoplay();
        }, 100);
      });

      this.initVisibilityObserver();
      this.updateControls();
      this.updatePosition(false);
      this.startAutoplay();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.stopAutoplay();
      window.clearTimeout(this.wheelSnapTimer);
      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.resizeTimer);
      window.cancelAnimationFrame(this.focusSyncFrame);
      this.visibilityObserver?.disconnect();
      this.cleanups.splice(0).forEach(cleanup => cleanup());
      this.slides.forEach(slide => {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      });
    }
  }

  class ProductsSliderController {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-products-slider-panel]');
      this.track = root.querySelector('[data-slider-track]');
      this.status = root.querySelector('[data-products-slider-status]');
      this.tablist = root.querySelector('.products-slider__tabs[role="tablist"]');
      this.tabs = this.tablist ? Array.from(this.tablist.querySelectorAll('.products-slider__tab[role="tab"]')) : [];
      this.cleanups = [];
      this.carousel = null;
      this.tabCache = new Map();
      this.tabRequest = null;
      this.loaderRequest = null;
      this.tabGeneration = 0;
      this.loaderGeneration = 0;
      this.destroyed = false;

      root.__productsSliderController = this;
      root.setAttribute('data-products-slider-initialized', 'true');
      this.initTabs();

      if (root.hasAttribute('data-recommendations')) this.loadRecommendations();
      else if (root.hasAttribute('data-recently-viewed')) this.loadRecentlyViewed();
      else this.rebuildCarousel();
    }

    text(name, fallback = '') {
      return this.root.dataset[name] || fallback;
    }

    setStatus(message, state = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      if (state) this.status.dataset.state = state;
      else delete this.status.dataset.state;
    }

    setBusy(busy) {
      this.panel?.setAttribute('aria-busy', String(busy));
      this.track?.classList.toggle('products-slider__track--loading', busy);
      this.track?.toggleAttribute('inert', busy);
      this.carousel?.setLoading(busy);
    }

    selectedTab() {
      return this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || null;
    }

    syncSelectedTab(tab) {
      if (!tab) return;
      this.tabs.forEach(candidate => {
        const selected = candidate === tab;
        candidate.classList.toggle('products-slider__tab--active', selected);
        candidate.setAttribute('aria-selected', String(selected));
        candidate.setAttribute('tabindex', selected ? '0' : '-1');
      });
      if (this.panel && tab.id) this.panel.setAttribute('aria-labelledby', tab.id);
      this.root.dataset.initialTabHandle = tab.dataset.tabHandle || '';
    }

    syncTabFocus(tab) {
      if (!tab || !this.tabs.includes(tab)) return;
      this.tabs.forEach(candidate => candidate.setAttribute('tabindex', candidate === tab ? '0' : '-1'));
    }

    moveTabFocus(currentTab, key) {
      const index = this.tabs.indexOf(currentTab);
      if (index < 0) return;
      let nextIndex = index;
      if (key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      else if (key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = this.tabs.length - 1;
      else return;

      this.tabs.forEach((tab, tabIndex) => tab.setAttribute('tabindex', tabIndex === nextIndex ? '0' : '-1'));
      this.tabs[nextIndex].focus();
    }

    initTabs() {
      if (!this.tablist || this.tabs.length === 0 || !this.track) return;
      const requestedHandle = this.root.dataset.initialTabHandle;
      const initial = this.tabs.find(tab => tab.dataset.tabHandle === requestedHandle)
        || this.selectedTab()
        || this.tabs[0];
      this.syncSelectedTab(initial);
      const handle = initial.dataset.tabHandle;
      if (handle) {
        const initialPayload = {
          html: this.track.innerHTML,
          empty: !this.track.querySelector(SLIDE_SELECTOR),
        };
        this.tabCache.set(handle, initialPayload);
        if (initialPayload.empty) this.setStatus(this.text('emptyText'), 'empty');
      }

      addListener(this.cleanups, this.tablist, 'click', event => {
        const tab = event.target.closest('.products-slider__tab[role="tab"]');
        if (!tab || !this.tablist.contains(tab)) return;
        tab.focus();
        this.syncTabFocus(tab);
        this.activateTab(tab);
      });
      addListener(this.cleanups, this.tablist, 'keydown', event => {
        const tab = event.target.closest('.products-slider__tab[role="tab"]');
        if (!tab || !this.tablist.contains(tab)) return;
        if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          this.moveTabFocus(tab, event.key);
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.activateTab(tab);
        }
      });
    }

    collectionUrl(handle) {
      const url = new URL(`collections/${encodeURIComponent(handle)}`, `${window.location.origin}${routesRoot(this.root)}`);
      url.searchParams.set('section_id', this.root.dataset.sectionId || '');
      return `${url.pathname}${url.search}`;
    }

    parseTrackResponse(html) {
      const parsed = new window.DOMParser().parseFromString(html, 'text/html');
      const nextTrack = parsed.querySelector('[data-slider-track]');
      if (!nextTrack) throw new Error('Products Slider response did not contain a track');
      return {
        html: nextTrack.innerHTML,
        empty: !nextTrack.querySelector(SLIDE_SELECTOR),
      };
    }

    clearPendingTabs() {
      this.tabs.forEach(tab => tab.classList.remove('products-slider__tab--pending'));
    }

    cancelTabRequest() {
      this.tabGeneration += 1;
      this.tabRequest?.abort();
      this.tabRequest = null;
      this.clearPendingTabs();
    }

    commitTab(tab, payload) {
      if (!this.track) return;
      const focusedTab = this.tabs.includes(document.activeElement) ? document.activeElement : null;
      const focusWasInPanel = this.panel?.contains(document.activeElement);
      if (focusWasInPanel) {
        try {
          tab.focus({ preventScroll: true });
        } catch {
          tab.focus();
        }
      }
      this.track.innerHTML = payload.html;
      this.syncSelectedTab(tab);
      if (focusWasInPanel) this.syncTabFocus(tab);
      else if (focusedTab) this.syncTabFocus(focusedTab);
      if (payload.empty) this.setStatus(this.text('emptyText'), 'empty');
      else this.setStatus('');
      this.rebuildCarousel();
    }

    async activateTab(tab) {
      if (this.destroyed || !tab || !this.tabs.includes(tab)) return;
      const handle = tab.dataset.tabHandle;
      if (!handle) return;
      if (tab === this.selectedTab() && !this.tabRequest) return;

      this.cancelTabRequest();
      if (this.tabCache.has(handle)) {
        this.setBusy(false);
        this.commitTab(tab, this.tabCache.get(handle));
        return;
      }

      const generation = ++this.tabGeneration;
      const request = new window.AbortController();
      this.tabRequest = request;
      tab.classList.add('products-slider__tab--pending');
      this.setBusy(true);
      this.setStatus(this.text('loadingText'), 'loading');

      try {
        const response = await window.fetch(this.collectionUrl(handle), { signal: request.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = this.parseTrackResponse(await response.text());
        if (this.destroyed || generation !== this.tabGeneration) return;
        this.tabCache.set(handle, payload);
        this.commitTab(tab, payload);
      } catch (error) {
        if (this.destroyed || generation !== this.tabGeneration || abortError(error)) return;
        const focusedTab = this.tabs.includes(document.activeElement) ? document.activeElement : null;
        this.syncSelectedTab(this.selectedTab() || this.tabs[0]);
        if (focusedTab) this.syncTabFocus(focusedTab);
        this.setStatus(this.text('errorText'), 'error');
        console.warn('Products Slider: Could not load collection', handle, error);
      } finally {
        if (!this.destroyed && generation === this.tabGeneration) {
          this.tabRequest = null;
          this.clearPendingTabs();
          this.setBusy(false);
        }
      }
    }

    rebuildCarousel() {
      this.carousel?.destroy();
      this.carousel = new CarouselController(this.root);
    }

    loaderController() {
      this.loaderRequest?.abort();
      this.loaderRequest = new window.AbortController();
      return { request: this.loaderRequest, generation: ++this.loaderGeneration };
    }

    loaderIsCurrent(generation) {
      return !this.destroyed && generation === this.loaderGeneration;
    }

    setControlsUnavailable(unavailable) {
      [
        this.root.querySelector('[data-slider-prev]'),
        this.root.querySelector('[data-slider-next]'),
      ].forEach(button => {
        if (!button) return;
        button.hidden = unavailable;
        button.disabled = unavailable;
      });
    }

    beginLoader() {
      this.setControlsUnavailable(true);
      this.setBusy(true);
      this.setStatus(this.text('loadingText'), 'loading');
      this.status?.classList.add('sr-only');
    }

    finishLoader() {
      this.rebuildCarousel();
      this.setBusy(false);
      this.setStatus('');
      this.status?.classList.remove('sr-only');
    }

    removeRoot() {
      const root = this.root;
      this.destroy();
      root.remove();
    }

    async loadRecommendations() {
      const productId = this.root.dataset.productId;
      const sectionId = this.root.dataset.sectionId;
      if (!productId || !sectionId) {
        console.warn('Products Slider recommendations: Missing product or section ID');
        this.removeRoot();
        return;
      }

      const { request, generation } = this.loaderController();
      this.beginLoader();
      const endpoint = this.root.dataset.recommendationsUrl || `${routesRoot(this.root)}recommendations/products`;
      try {
        const htmlResponse = await window.DrhRecommendations.request({
          endpoint, productId, sectionId, limit: this.root.dataset.maxProducts || 10,
          intent: this.root.dataset.intent || 'related', signal: request.signal,
        });
        const payload = this.parseTrackResponse(htmlResponse);
        if (!this.loaderIsCurrent(generation)) return;
        if (payload.empty) {
          this.removeRoot();
          return;
        }
        this.track.innerHTML = payload.html;
        this.root.classList.add('is-loaded');
        this.finishLoader();
      } catch (error) {
        if (!this.loaderIsCurrent(generation) || abortError(error)) return;
        console.warn('Products Slider: Could not load recommendations', error);
        this.removeRoot();
      }
    }

    async loadRecentlyViewed() {
      const { request, generation } = this.loaderController();
      this.beginLoader();
      const currentProduct = this.root.dataset.currentProduct;
      const maximum = parseNumber(this.root.dataset.maxProducts, 12);
      let recentlyViewed;

      try {
        recentlyViewed = JSON.parse(window.localStorage.getItem('drhoelter_recently_viewed')) || [];
      } catch {
        recentlyViewed = [];
      }
      if (!Array.isArray(recentlyViewed)) recentlyViewed = [];

      recentlyViewed = recentlyViewed
        .map(item => typeof item === 'string' ? { handle: item } : item)
        .filter(item => item?.handle && item.handle !== currentProduct)
        .slice(0, maximum);

      if (recentlyViewed.length === 0) {
        this.removeRoot();
        return;
      }

      const ratingMap = {};
      recentlyViewed.forEach(item => {
        if (item.rating || item.ratingCount) {
          ratingMap[item.handle] = { rating: item.rating || 0, ratingCount: item.ratingCount || 0 };
        }
      });

      try {
        const products = await Promise.all(recentlyViewed.map(async item => {
          try {
            const response = await window.fetch(
              `${routesRoot(this.root)}products/${encodeURIComponent(item.handle)}.js`,
              { signal: request.signal }
            );
            return response.ok ? await response.json() : null;
          } catch (error) {
            if (abortError(error)) throw error;
            return null;
          }
        }));
        if (!this.loaderIsCurrent(generation)) return;
        const validProducts = products.filter(Boolean);
        if (validProducts.length === 0) {
          this.removeRoot();
          return;
        }

        this.track.innerHTML = validProducts.map(product => this.productCardHtml(
          product,
          ratingMap[product.handle] || null
        )).join('');
        this.root.classList.add('is-loaded');
        this.finishLoader();
      } catch (error) {
        if (!this.loaderIsCurrent(generation) || abortError(error)) return;
        console.warn('Products Slider: Could not load recently viewed products', error);
        this.removeRoot();
      }
    }

    productCardHtml(product, storedRating) {
      const detailed = this.root.dataset.cardType === 'detailed';
      const showRating = this.root.dataset.showRating !== 'false';
      const showRatingValue = this.root.dataset.showRatingValue === 'true';
      const showRatingCount = this.root.dataset.showRatingCount === 'true';
      const showVendor = this.root.dataset.showVendor !== 'false';
      const showPrice = this.root.dataset.showPrice !== 'false';
      const showComparePrice = this.root.dataset.showComparePrice !== 'false';
      const showBadges = this.root.dataset.showBadges !== 'false';
      const showHoverImage = this.root.dataset.showHoverImage === 'true';
      const showQuickBuy = this.root.dataset.showQuickBuy === 'true';
      const showDescription = detailed && this.root.dataset.showDescription !== 'false';
      const imageRatio = ['portrait', 'landscape'].includes(this.root.dataset.imageRatio)
        ? this.root.dataset.imageRatio
        : 'square';
      const descriptionLength = parseNumber(this.root.dataset.descriptionLength, 80);
      const image = product.featured_image || product.images?.[0];
      const hoverImage = product.images?.[1];
      const imageUrl = this.sizedImageUrl(image, '200x');
      const hoverImageUrl = this.sizedImageUrl(hoverImage, '200x');
      const productUrl = this.productUrl(product);
      const comparePrice = product.compare_at_price > product.price ? this.formatMoney(product.compare_at_price) : '';
      const priceVaries = product.price_varies === true;

      let description = '';
      if (showDescription && product.description) {
        const temporary = document.createElement('div');
        temporary.innerHTML = product.description;
        description = temporary.textContent || '';
        if (description.length > descriptionLength) description = `${description.slice(0, descriptionLength).trim()}...`;
      }

      const rating = showRating ? this.ratingHtml(storedRating, showRatingValue, showRatingCount) : '';
      const badge = showBadges && comparePrice
        ? `<span class="product-card-slider__badge product-card-slider__badge--sale">${this.escapeHtml(this.text('saleText'))}</span>`
        : '';
      const hover = showHoverImage && hoverImageUrl
        ? `<img src="${this.escapeHtml(hoverImageUrl)}" alt="" loading="lazy" width="200" height="200" aria-hidden="true" class="product-card-slider__image product-card-slider__image--hover">`
        : '';
      const quickBuy = showQuickBuy && product.available
        ? `<a href="${this.escapeHtml(productUrl)}" class="product-card-slider__quick-buy">${this.escapeHtml(this.text('moreInfoText'))}</a>`
        : '';

      return `
        <div class="products-slider__slide">
          <article class="product-card-slider product-card-slider--${detailed ? 'default' : 'compact'}">
            <a href="${this.escapeHtml(productUrl)}" class="product-card-slider__link">
              <div class="product-card-slider__image-wrapper product-card-slider__image-wrapper--${imageRatio}">
                ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="200" height="200" class="product-card-slider__image">` : ''}
                ${hover}${badge}
              </div>
              <div class="product-card-slider__info">
                ${rating}
                ${showVendor && product.vendor ? `<p class="product-card-slider__vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
                <h3 class="product-card-slider__title">${this.escapeHtml(product.title)}</h3>
                ${description ? `<p class="product-card-slider__description">${this.escapeHtml(description)}</p>` : ''}
                ${showPrice ? `<div class="product-card-slider__price">${priceVaries ? `<span class="product-card-slider__price-from">${this.escapeHtml(this.text('fromText'))} </span>` : ''}<span class="product-card-slider__price-amount">${this.formatMoney(product.price)}</span>${showComparePrice && comparePrice ? `<span class="product-card-slider__price-compare">${comparePrice}</span>` : ''}</div>` : ''}
              </div>
            </a>
            ${quickBuy}
          </article>
        </div>`;
    }

    ratingHtml(ratingData, showValue, showCount) {
      const rating = Number(ratingData?.rating || 0);
      const count = Number(ratingData?.ratingCount || 0);
      if (!(rating > 0) || !(count > 0)) return '';
      const fullStars = Math.floor(rating);
      const hasHalf = rating % 1 >= 0.3;
      let stars = '';
      for (let index = 1; index <= 5; index += 1) {
        if (index <= fullStars) stars += this.starIcon('filled');
        else if (index === fullStars + 1 && hasHalf) stars += this.starIcon('half');
        else stars += this.starIcon('empty');
      }
      const label = `${Math.round(rating * 10) / 10}/5`;
      return `<div class="product-card__rating" aria-label="${this.escapeHtml(label)}"><div class="product-card__stars" aria-hidden="true">${stars}</div>${showValue ? `<span class="product-card__rating-value">${rating.toFixed(1)}/5</span>` : ''}${showCount ? `<span class="product-card__rating-count">(${count})</span>` : ''}</div>`;
    }

    starIcon(kind) {
      const path = 'M8 0L10.3511 4.76393L15.6085 5.52786L11.8042 9.23607L12.7023 14.4721L8 12L3.29772 14.4721L4.19577 9.23607L0.391548 5.52786L5.64886 4.76393L8 0Z';
      if (kind === 'half') {
        return `<svg class="star-half" width="16" height="16" viewBox="-0.5 -0.5 17 15.5" fill="none" aria-hidden="true"><path d="${path}" fill="currentColor" fill-opacity="0.25" stroke="currentColor" stroke-width="1"></path><path d="M8 0L5.64886 4.76393L0.391548 5.52786L4.19577 9.23607L3.29772 14.4721L8 12V0Z" fill="currentColor"></path></svg>`;
      }
      const attributes = kind === 'filled'
        ? 'fill="currentColor"'
        : 'stroke="currentColor" stroke-width="1" fill="currentColor" fill-opacity="0.25"';
      return `<svg class="star-${kind}" width="16" height="16" viewBox="-0.5 -0.5 17 15.5" fill="none" aria-hidden="true"><path d="${path}" ${attributes}></path></svg>`;
    }

    formatMoney(cents) {
      const amount = Number(cents);
      if (!Number.isFinite(amount)) return '';
      const moneyFormat = this.root.dataset.moneyFormat || '';
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(amount, moneyFormat);
      }
      const locale = this.root.dataset.locale || document.documentElement.lang || undefined;
      const currency = window.Shopify?.currency?.active || this.root.dataset.currencyCode;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount / 100);
      } catch {
        return (amount / 100).toFixed(2);
      }
    }

    productUrl(product) {
      const rootPath = routesRoot(this.root);
      const fallback = `${rootPath}products/${encodeURIComponent(product?.handle || '')}`;
      const source = product?.url || fallback;
      try {
        const url = new URL(source, window.location.origin);
        if (
          url.origin === window.location.origin
          && rootPath !== '/'
          && url.pathname.startsWith('/products/')
          && !url.pathname.startsWith(rootPath)
        ) {
          url.pathname = `${rootPath.slice(0, -1)}${url.pathname}`;
        }
        return url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.href;
      } catch {
        return fallback;
      }
    }

    sizedImageUrl(source, size) {
      if (!source) return '';
      if (source.includes(`_${size}`)) return source;
      const match = source.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      return match ? source.replace(match[0], `_${size}${match[0]}`) : source;
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value || '';
      return element.innerHTML;
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.cancelTabRequest();
      this.loaderGeneration += 1;
      this.loaderRequest?.abort();
      this.loaderRequest = null;
      this.carousel?.destroy();
      this.carousel = null;
      this.setBusy(false);
      this.setStatus('');
      this.status?.classList.remove('sr-only');
      this.cleanups.splice(0).forEach(cleanup => cleanup());
      this.root.removeAttribute('data-products-slider-initialized');
      delete this.root.__productsSliderController;
    }
  }

  const pendingLazyRoots = new WeakMap();

  const manager = {
    initScope(scope = document) {
      rootsInScope(scope).forEach(root => {
        if (root.__productsSliderController || pendingLazyRoots.has(root)) return;
        const lazy = root.dataset.loadStrategy === 'lazy';
        if (lazy && typeof window.IntersectionObserver === 'function') {
          const observer = new window.IntersectionObserver(entries => {
            if (pendingLazyRoots.get(root) !== observer) return;
            if (!entries.some(entry => entry.target === root && entry.isIntersecting)) return;
            observer.disconnect();
            if (pendingLazyRoots.get(root) === observer) pendingLazyRoots.delete(root);
            if (root.isConnected && !root.__productsSliderController) new ProductsSliderController(root);
          }, { rootMargin: '200px 0px', threshold: 0 });
          pendingLazyRoots.set(root, observer);
          observer.observe(root);
        } else {
          new ProductsSliderController(root);
        }
      });
    },

    destroyScope(scope = document) {
      rootsInScope(scope).forEach(root => {
        pendingLazyRoots.get(root)?.disconnect();
        pendingLazyRoots.delete(root);
        root.__productsSliderController?.destroy();
      });
    },
  };

  window.DrhProductsSlider = manager;

  const initialize = () => manager.initScope(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  document.addEventListener('shopify:section:load', event => manager.initScope(event.target));
  document.addEventListener('shopify:section:unload', event => manager.destroyScope(event.target));
})();
